import { uuidv4 } from "./uuid";

const VISITOR_COOKIE = "_xp_vid";
const SESSION_KEY = "_xp_sess";
const VISITOR_TTL_DAYS = 180;
const SESSION_IDLE_MS = 30 * 60 * 1000;

interface StoredSession {
  id: string;
  last: number;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = name + "=";
  const parts = document.cookie ? document.cookie.split(";") : [];
  for (const part of parts) {
    const p = part.trim();
    if (p.indexOf(target) === 0) return decodeURIComponent(p.substring(target.length));
  }
  return null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

export function ensureVisitorId(persistent: boolean): string | null {
  if (!persistent) return null;
  let id = readCookie(VISITOR_COOKIE);
  if (!id) {
    id = uuidv4();
    writeCookie(VISITOR_COOKIE, id, VISITOR_TTL_DAYS);
  }
  return id;
}

/**
 * Session id lives in a first-party cookie (`_xp_sess` = "<id>.<lastActivityMs>"),
 * NOT in sessionStorage: sessionStorage is per-tab, so every internal link
 * with target="_blank" and every return from an external widget in a new tab
 * used to start a brand-new session with the site's own domain as referrer
 * (~20% inflated session counts on hellopark-avk.ru). sessionStorage is kept
 * only as a fallback for browsers that refuse the cookie.
 *
 * The cookie is refreshed on every tracked event (throttled), so a session
 * ends 30 minutes after the last activity, not 30 minutes after page load.
 */
const SESSION_TTL_S = SESSION_IDLE_MS / 1000;
const TOUCH_THROTTLE_MS = 10 * 1000;

function parseStored(raw: string | null): StoredSession | null {
  if (!raw) return null;
  // cookie format: "<uuid>.<ms>"
  const dot = raw.lastIndexOf(".");
  if (dot > 0) {
    const id = raw.slice(0, dot);
    const last = Number(raw.slice(dot + 1));
    if (id && Number.isFinite(last)) return { id, last };
  }
  // legacy sessionStorage JSON format
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed && typeof parsed.id === "string" && typeof parsed.last === "number") return parsed;
  } catch {
    // fallthrough
  }
  return null;
}

function writeSessionCookie(sess: StoredSession): void {
  if (typeof document === "undefined") return;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${SESSION_KEY}=${encodeURIComponent(sess.id + "." + sess.last)}; Max-Age=${SESSION_TTL_S}; Path=/; SameSite=Lax${secure}`;
}

function persistSession(sess: StoredSession): void {
  writeSessionCookie(sess);
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

let currentSession: StoredSession | null = null;
let lastTouchAt = 0;

export function ensureSessionId(): { sessionId: string; isNew: boolean } {
  const now = Date.now();

  let stored = parseStored(readCookie(SESSION_KEY));
  if (!stored) {
    try {
      stored = parseStored(sessionStorage.getItem(SESSION_KEY));
    } catch {
      stored = null;
    }
  }

  if (stored && now - stored.last < SESSION_IDLE_MS) {
    stored.last = now;
    persistSession(stored);
    currentSession = stored;
    lastTouchAt = now;
    return { sessionId: stored.id, isNew: false };
  }

  const fresh: StoredSession = { id: uuidv4(), last: now };
  persistSession(fresh);
  currentSession = fresh;
  lastTouchAt = now;
  return { sessionId: fresh.id, isNew: true };
}

/** Extend the session on activity. Cheap: at most one cookie write per 10s. */
export function touchSession(): void {
  if (!currentSession) return;
  const now = Date.now();
  if (now - lastTouchAt < TOUCH_THROTTLE_MS) return;
  lastTouchAt = now;
  currentSession.last = now;
  persistSession(currentSession);
}

export function newPageInstanceId(): string {
  return uuidv4();
}

export function newEventId(): string {
  return uuidv4();
}
